import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const titleCase = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CategoryPage() {
  const { slug } = useParams();
  const [posts, setPosts] = useState(null);
  const name = titleCase(slug || "");

  useSEO({
    title: `${name} — Articles & Guides | Training Basket Blog`,
    description: `Read Training Basket articles about ${name.toLowerCase()} — practical guides, career advice and course insights.`,
    canonical: `/blog/category/${slug}`,
  });

  useEffect(() => {
    setPosts(null);
    api.get("/posts")
      .then((r) => setPosts((r.data.posts || []).filter((p) => slugify(p.category) === slug)))
      .catch(() => setPosts([]));
  }, [slug]);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-category-page">
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700" data-testid="category-back">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#17263B] tracking-tight mt-4" data-testid="category-title">
            {name}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            {posts ? `${posts.length} article${posts.length === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {posts === null ? (
          <div className="h-48 rounded-3xl bg-slate-100 animate-pulse" />
        ) : posts.length === 0 ? (
          <div className="text-center py-20" data-testid="category-empty">
            <p className="font-bold text-lg text-[#17263B]">Articles coming soon</p>
            <p className="text-sm text-slate-500 mt-2">We're preparing fresh content for this topic.</p>
            <Link to="/blog" className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-rose-600">
              Browse all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-grid">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`category-post-${p.slug}`}
                className="group rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md hover:border-rose-200 transition-all bg-white">
                {p.image && <img src={p.image} alt={p.title} className="w-full h-40 object-cover" loading="lazy" />}
                <div className="p-5">
                  <h2 className="font-bold text-[#17263B] leading-snug group-hover:text-rose-700 transition-colors">{p.title}</h2>
                  {p.excerpt && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.excerpt}</p>}
                  <p className="text-xs text-slate-400 mt-3">{p.author}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
